import { toast } from 'sonner';
import { getAuthenticatedSupabaseClient } from '@/integrations/supabase/client';
import {
  AppError,
  AuthenticationError,
  AuthorizationError,
  NetworkError,
} from './errors';
import { useCallback } from 'react';
import { logger } from '@/lib/logger';

interface ErrorContext {
  userId?: string;
  action?: string;
  resource?: string;
  metadata?: Record<string, any>;
}

class ErrorHandler {
  private isDevelopment = import.meta.env.DEV;

  async handle(error: unknown, context?: ErrorContext): Promise<void> {
    const appError = this.normalizeError(error);
    
    // Log error details in development
    if (this.isDevelopment) {
      logger.error('Error:', appError);
      logger.error('Context:', context);
    }

    // Show user-friendly toast
    this.showToast(appError);

    // Log to error tracking service
    await this.logError(appError, context);

    // Handle specific error types
    this.handleSpecificErrors(appError);
  }

  private normalizeError(error: unknown): AppError {
    if (error instanceof AppError) {
      return error;
    }

    if (error instanceof Error) {
      // Handle Supabase errors
      if (error.message.includes('JWT')) {
        return new AuthenticationError('Session expired. Please sign in again.');
      }

      if (error.message.includes('Row Level Security')) {
        return new AuthorizationError();
      }

      if (error.message.includes('NetworkError') || error.message.includes('fetch')) {
        return new NetworkError();
      }

      return new AppError(error.message, 'UNKNOWN_ERROR', 500);
    }

    return new AppError('An unexpected error occurred', 'UNKNOWN_ERROR', 500);
  }

  private showToast(error: AppError): void {
    const messages: Record<string, string> = {
      VALIDATION_ERROR: error.message,
      AUTHENTICATION_ERROR: 'Please sign in to continue',
      AUTHORIZATION_ERROR: 'You don\'t have permission to perform this action',
      NOT_FOUND: error.message,
      NETWORK_ERROR: 'Connection error. Please check your internet connection.',
      UNKNOWN_ERROR: 'Something went wrong. Please try again.',
    };

    const message = messages[error.code] || messages.UNKNOWN_ERROR;

    toast.error(message, {
      duration: 5000,
      action: error.code === 'AUTHENTICATION_ERROR' ? {
        label: 'Sign In',
        onClick: () => window.location.href = '/signin',
      } : undefined,
    });
  }

  private async logError(error: AppError, context?: ErrorContext): Promise<void> {
    try {
      const supabase = await getAuthenticatedSupabaseClient();
      // Log to Supabase error_logs table
      const { error: dbError } = await supabase.from('error_logs').insert({
        error_code: error.code,
        error_message: error.message,
        error_details: error.details,
        context,
        user_id: context?.userId,
        created_at: new Date().toISOString(),
      });

      if (dbError) {
        // If table doesn't exist or other DB error, just log to console
        if (this.isDevelopment) {
          logger.warn('Could not log error to database:', dbError.message);
        }
      }
    } catch (logError) {
      // Silently fail in production, log in development
      if (this.isDevelopment) {
        logger.warn('Failed to log error to database:', logError);
      }
    }
  }

  private handleSpecificErrors(error: AppError): void {
    if (error instanceof AuthenticationError) {
      // Redirect to login after a delay
      setTimeout(() => {
        window.location.href = '/signin';
      }, 2000);
    }
  }
}

export const errorHandler = new ErrorHandler();

// React Query error handler
export function queryErrorHandler(error: unknown): void {
  errorHandler.handle(error, { action: 'query' });
}

// React hook for error handling
export function useErrorHandler() {
  return useCallback((error: unknown, context?: ErrorContext) => {
    errorHandler.handle(error, context);
  }, []);
}
